<!-- eslint-disable vue/multi-word-component-names -->
<script>
import { LMap, LMarker, LTileLayer, LPopup, LControlZoom } from 'vue2-leaflet';
import MapSearchField from '@/components/app/map/search.vue';

export default {
  components: {
    LMap,
    LMarker,
    LTileLayer,
    LPopup,
    MapSearchField,
    LControlZoom,
  },
  props: {
    searchable: {
      type: Boolean,
      required: false,
      default: true,
    },
    justFocusOnSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    clickable: {
      type: Boolean,
      required: false,
      default: true,
    },
    draggable: {
      type: Boolean,
      required: false,
      default: true,
    },
    zoom: {
      type: Number,
      required: false,
      default: 13,
    },
    value: {
      type: Object,
      required: false,
      default: () => ({
        lat: null,
        lng: null,
      }),
    },
    center: {
      type: Array,
      required: false,
      default: () => [35.7219, 51.3347],
    },
    mapTitle: {
      type: String,
      required: false,
      default: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    },
    popupText: {
      type: String,
      required: false,
      default: 'مکان انتخاب شده',
    },
  },
  data() {
    return {
      mapObject: null,
      timer: null,
      defaultMarkerPosition: {
        lat: this.value?.lat || null,
        lng: this.value?.lng || null
      },
    };
  },
  computed: {
    _zoom() {
      return this.$store.state.SystemSettingModule.systemSetting?.mapDefaultZoomLevel ?? this.zoom;
    },
    _mapTileLayerUrl() {
      return this.$store.state.SystemSettingModule.systemSetting?.mapTile ?? this.mapTitle;
    },
    _center() {
      if (this.defaultMarkerPosition?.lat && this.defaultMarkerPosition?.lng) {
        return [this.defaultMarkerPosition.lat, this.defaultMarkerPosition.lng];
      }
      return this.$store.state.SystemSettingModule.systemSetting?.mapDefaultCenter ?? this.center;
    },
  },
  mounted() {
    this.clearTimer();
    this.initialMap();
  },
  unmounted() {
    this.clearTimer();
  },
  methods: {
    removeMarker() {
      this.defaultMarkerPosition = { lat: null, lng: null };
    },
    initialMap: function () {
      this.mapObject = this.$refs.defaultMap?.mapObject;
      setTimeout(() => {
        this.$refs.defaultMap.mapObject.invalidateSize();
      }, 100);
    },
    onClickMap: function (e) {
      if (this.clickable) {
        this.defaultMarkerPosition = e.latlng;
      }
    },
    onSearchSuggestion: function (latLng) {
      if (!this.justFocusOnSearch) {
        this.defaultMarkerPosition = latLng;
      }
    },
    onMarkerDragEnd: function (e) {
      const latlng = e.target.getLatLng();
      this.defaultMarkerPosition.lat = latlng.lat;
      this.defaultMarkerPosition.lng = latlng.lng;
    },
    clearTimer: function () {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    onMarkerReady() {
      this.autoClickTimer = setTimeout(() => {
        this.autoClickMarkerOpen();
      }, 1000);
    },
    autoClickMarkerOpen() {
      this.$nextTick(() => {
        if (this.$refs.markerClick?.mapObject) {
          this.$refs.markerClick.mapObject.openPopup();
        }
        if (this.$refs.addressPopup?.mapObject) {
          this.$refs.addressPopup.mapObject.openPopup();
        }
      });
    },
    autoClickMarkerClose() {
      this.$nextTick(() => {
        if (this.$refs.markerClick?.mapObject) {
          this.$refs.markerClick.mapObject.closePopup();
        }
        if (this.$refs.addressPopup?.mapObject) {
          this.$refs.addressPopup.mapObject.closePopup();
        }
      });
    },
  },
  watch: {
    defaultMarkerPosition: {
      handler(newVal) {
        this.$emit('input', newVal);
        this.timer = setTimeout(() => {
          this.mapObject?.flyTo([newVal?.lat, newVal?.lng], 16);
        }, 100);
        this.autoClickTimer = setTimeout(() => {
          this.autoClickMarkerOpen();
        }, 1000);
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
<template>
  <div class="map-wrapper">
    <div class="map-header">
      <div v-if="searchable" class="search-container">
        <map-search-field @address-search="onSearchSuggestion" boxTextTitle="search-title" />
      </div>
      <slot name="header" :lat-lng="latLng"></slot>
      <slot name="btn_BTS"></slot>
    </div>

    <div class="map-body">
      <l-map v-if="_mapTileLayerUrl" ref="defaultMap" :zoom="_zoom" :center="_center" @ready="initialMap"
        @click="onClickMap" :options="{ zoomControl: false }">
        <l-tile-layer :url="_mapTileLayerUrl" attribution="&copy; Asemantech" />
        <slot name="body"></slot>

        <l-marker v-if="defaultMarkerPosition?.lat" :lat-lng="[defaultMarkerPosition.lat, defaultMarkerPosition.lng]"
          :icon="getIcon" :draggable="draggable" @dragend="onMarkerDragEnd" ref="markerClick" @ready="onMarkerReady">
          <slot name="default-popup" :lat-lng="defaultMarkerPosition">
            <l-popup v-if="popupText">{{ popupText }}</l-popup>
          </slot>
        </l-marker>

        <l-control-zoom position="bottomleft"></l-control-zoom>
      </l-map>
    </div>

    <slot name="append"></slot>
  </div>
</template>
<style scoped>
.map-wrapper {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
}

.map-header {
  width: 100%;
  position: absolute;
  z-index: 10;
  top: 1rem;
  right: 0;
  padding-left: 1rem;
  padding-right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-container {
  max-width: 65% !important;
  width: 65% !important;
  width: 2.5rem;
  flex: 1 1 auto;
}

.search-title {
  max-width: 65% !important;
  width: 65% !important;
}

.map-body {
  flex: 1 1 auto;
  height: 2.5rem;
  position: relative;
  z-index: 1;
  overflow: hidden;
}
</style>
